import { useRef, useState, useEffect } from 'react'

type Direction = 'horizontal' | 'vertical'

type UseVisibleItemsProps = {
  totalItems: number
  maxLimit: number
  itemSize: number
  gap?: number
  direction?: Direction
  hasButton?: boolean
  buttonSize?: number
}

export const useVisibleItems = <T extends HTMLElement = HTMLElement>({
  totalItems,
  maxLimit,
  itemSize,
  gap = 4,
  direction = 'horizontal',
  hasButton = false,
  buttonSize,
}: UseVisibleItemsProps) => {
  const containerRef = useRef<T>(null)
  const [visibleItemsCount, setVisibleItemsCount] = useState(maxLimit)

  const calculateVisibleItems = () => {
    if (!containerRef.current) return

    const containerSize =
      direction === 'horizontal'
        ? containerRef.current.offsetWidth
        : containerRef.current.offsetHeight

    let availableSize = containerSize

    if (hasButton) {
      const btnSize = buttonSize || itemSize
      availableSize -= btnSize + gap
    }

    const itemsFit = Math.floor((availableSize + gap) / (itemSize + gap))
    const newCount = Math.max(1, Math.min(itemsFit, maxLimit))
    setVisibleItemsCount(newCount)
  }

  useEffect(() => {
    calculateVisibleItems()

    const resizeObserver = new ResizeObserver(calculateVisibleItems)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [totalItems, maxLimit, itemSize, gap, direction, hasButton, buttonSize])

  return {
    containerRef,
    visibleItemsCount,
    isLimitReached: totalItems > visibleItemsCount,
  }
}

export default useVisibleItems
